/**
 * PWA install-prompt plumbing for the admin app (EVI Admin).
 *
 * Mirrors the driver app's lib/pwaInstall.ts exactly: it captures the install
 * prompt and detects whether the app is already running standalone (installed).
 * No caching is involved anywhere (see public/sw.js — a pure passthrough that
 * only satisfies Chrome's install criteria).
 *
 * The `beforeinstallprompt` event (Chromium: Android + desktop) can fire before
 * any React component mounts, so we attach the listener once at boot and stash
 * the event behind a tiny external store the InstallAppButton subscribes to.
 */

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let deferred: BeforeInstallPromptEvent | null = null;
let installed = false;
const listeners = new Set<() => void>();
const emit = (): void => {
  for (const l of listeners) l();
};

/** Attach the capture listeners once, at boot (before the event fires). */
export function initInstallPrompt(): void {
  if (typeof window === 'undefined') return;
  window.addEventListener('beforeinstallprompt', (e) => {
    // Stop Chrome's mini-infobar so our own button owns the timing.
    e.preventDefault();
    deferred = e as BeforeInstallPromptEvent;
    emit();
  });
  window.addEventListener('appinstalled', () => {
    deferred = null;
    installed = true;
    emit();
  });
}

/** Subscribe for store changes (useSyncExternalStore). */
export function subscribeInstall(cb: () => void): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

/** Snapshot: true when a native install prompt is ready to fire. */
export function canInstall(): boolean {
  return deferred !== null && !installed;
}

/** Fire the captured native install prompt; resolves to the outcome. */
export async function promptInstall(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
  if (!deferred) return 'unavailable';
  const e = deferred;
  // A prompt can only be used once; clear before awaiting so the button hides.
  deferred = null;
  emit();
  await e.prompt();
  const { outcome } = await e.userChoice;
  if (outcome === 'accepted') installed = true;
  return outcome;
}

/** True when already running as an installed app (standalone display). */
export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  const mql = window.matchMedia?.('(display-mode: standalone)');
  const iosStandalone = (window.navigator as Navigator & { standalone?: boolean }).standalone === true;
  return mql?.matches === true || iosStandalone;
}

/**
 * iOS Safari only: no `beforeinstallprompt`, so install is manual via the
 * Share sheet. Chrome/Firefox on iOS can't install PWAs, so this gates the
 * "Add to Home Screen" hint to iOS Safari specifically.
 */
export function isIosSafari(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  const iPhoneLike = /iPad|iPhone|iPod/.test(ua);
  // iPadOS 13+ masquerades as Mac — detect by touch points.
  const iPadOS =
    navigator.platform === 'MacIntel' &&
    (navigator as Navigator & { maxTouchPoints: number }).maxTouchPoints > 1;
  const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS/.test(ua);
  return (iPhoneLike || iPadOS) && isSafari;
}
